import React from 'react';
import useSWR from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { createApiRequest, fetcherWithDataKey } from '@/utils/services';

type ParamType = {
  page?: number;
  pageSize?: number;
};

const API_BASE = 'automations/major-gift';

const useMajorGiftAutomations = ({
  page = 1,
  pageSize = 1000, // We're going to show all automations for now and not bother with pagination.
}: ParamType = {}) => {
  const ownerOrgId = useOwnerOrgId();
  const key = ownerOrgId
    ? `${API_BASE}?${joinNonEmptyParams({
        ownerOrgId,
        pageSize,
        pageFrom: (page - 1) * pageSize,
      })}`
    : null;

  const { data, error, mutate } = useSWR<MajorGiftAutomation[]>(
    key,
    fetcherWithDataKey('mgAutomations'),
  );

  const create = React.useCallback(
    async (
      automationData: Omit<MajorGiftAutomation, 'automationId' | 'ownerOrgId'>,
    ) => {
      const res: APIResponse<{ mgAutomation: MajorGiftAutomation }> =
        await createApiRequest({
          data: {
            ownerOrgId,
            ...automationData,
          },
          method: 'post',
          path: API_BASE,
        });
      const newAutomation = res.data.mgAutomation;
      mutate((oldVal) => [newAutomation, ...(oldVal || [])], {
        revalidate: false,
      });
      return newAutomation;
    },
    [mutate, ownerOrgId],
  );

  const remove = React.useCallback(
    async (automationId: string) => {
      await createApiRequest({
        method: 'delete',
        path: `${API_BASE}/${automationId}`,
        throwErrors: true,
      });
      return mutate(
        (oldVal) =>
          (oldVal || []).filter((item) => item.automationId !== automationId),
        { revalidate: false },
      );
    },
    [mutate],
  );

  const update = React.useCallback(
    async (automationId: string, patch: Partial<MajorGiftAutomation> = {}) => {
      const res: APIResponse<{ mgAutomation: MajorGiftAutomation }> =
        await createApiRequest({
          data: patch,
          method: 'put',
          path: `${API_BASE}/${automationId}`,
        });
      const newAutomation = res.data.mgAutomation;
      mutate(
        (oldVal) =>
          (oldVal || []).map((item) =>
            item.automationId === automationId ? newAutomation : item,
          ),
        { revalidate: false },
      );
      return newAutomation;
    },
    [mutate, ownerOrgId],
  );

  return { create, data, error, remove, update };
};

export default useMajorGiftAutomations;
